<template>
    <wrapper-features-templates-one-column
    nonAuthenticatedFeature
    :customBreadcrumbs="customBreadcrumbs"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :showFooter="!showLoader && !applyErrorState"
    :footerButtonsParams="footerButtonsParams.filter(ele => !ele.hidden)"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <div v-if="!showLoader" class="content-wrapper">
            <div class="text">{{ t('preActiveMode.topMessage') }}</div>
            <!-- Text Block -->
            <text-block-list
            :list="list"
            :isNumbered="true"/>
            <div class="token-content-wrapper" :class="{'mobile': getMobileAppState}">
                <div class="activationCode">{{ t('preActiveMode.activationCode') }}</div>
                <div class="inputs-wrapper" :class="{'adjust-margin': applyErrorState && code.join('').length < 4 }">
                    <!-- Input OTP -->
                    <input-otp
                    v-for="position in 4"
                    ref="otpInputRef"
                    :errorState="showMandatoryErrors && !code[position-1]"
                    :key="position"
                    :value="code[position-1]"
                    @backspaceHandler="backspaceHandler(position, $event)"
                    @inputPositionChanged="inputPositionChanged(position, $event)"
                    @pasteData="pasteData"/>
                </div>
                <!-- Error Message -->
                <div v-if="showMandatoryErrors && code.join('').length < 4" class="error-message">
                    {{ t('mandatoryFieldError') }}
                </div>
            </div>
            <!-- Resend info and button -->
            <div class="resent-wrapper">
                <div class="title-note">{{ t('preActiveMode.token.titleNote') }}</div>
                <div class="hour">{{parseTimer(timer)}}</div>
                <div class="resend-message"> {{ t('preActiveMode.token.note') }}</div>
                <button-link
                internalClick
                :label="t('modals.token.resendBtn')"
                @clicked="requestNewTokenHandler"/>
            </div>
        </div>
    </wrapper-features-templates-one-column>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import ToastsGeneric from '@/components/generic/toasts/Generic'
import TextBlockList from '@/components/generic/textBlocks/List'
import InputOtp from '@/components/generic/inputs/OTP'
import ButtonLink from '@/components/generic/buttons/Link'

/* Vue  */
import { ref, computed } from 'vue'

/* Assets & Helpers */
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { parseTimer } from '@/assets/scripts/dateBuilders'
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { consultAdhesionProcess, finishAdhesionProcess, resendFinishAdhesionProcessToken } = mapActions(moduleMapper.PRE_ACTIVE_MODE)

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'showFeedbackToast'])

/* props received
@processId { String } - processId
*/
const props = defineProps({
    processId: String
})

/* Generic Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(false)
const code = ref([null, null, null, null])
const otpInputRef = ref('')
const currentProcessInfo = ref(null)
const showMandatoryErrors = ref(false)

const customBreadcrumbs = {
    currentArea: t('preActiveMode.customBreadcrumbs.currentArea')
}

const list = [
    t('preActiveMode.list.list1'),
    t('preActiveMode.list.list2'),
    t('preActiveMode.list.list3'),
    t('preActiveMode.list.list4'),
    t('preActiveMode.list.list5')
]

/*************************************************
*                                                *
*                 OTP Input Logic                *
*                                                *
*************************************************/
const backspaceHandler = (position, $event) => {
    /* If our position has no content we will back */
    if ((!code.value[position - 1] || !code.value[position - 1].length) && (position - 2 >= 0)) {
        otpInputRef.value[position - 2].$el.children[0].focus()
        code.value[position - 2] = ''
        $event.stopPropagation()
        $event.preventDefault()
    }
}

const regexValidator = string => {
	const regex = /^[0-9]{0,1}$/
	return regex.test(string)
}

const inputPositionChanged = (position, value) => {
    showMandatoryErrors.value = false
    if (!regexValidator(value)) {
        code.value[position - 1] = ''
        return
    }

    code.value[position - 1] = value
    /* it focus the next input and if it's the last only blurs, leaving no inputs focused */
    if (value) {
        if (position < 4) {
            otpInputRef.value[position].$el.children[0].focus()
        } else {
            otpInputRef.value[position - 1].$el.children[0].blur()
        }
    } else {
        if (position > 1) {
            otpInputRef.value[0].$el.children[0].focus()
        }
    }
}

/* handles when user pastes the code */
const pasteData = async $event => {
    const text = $event.clipboardData.getData('text')
    code.value = text.replace(/(?:[^0-9])/g, '').substring(0, 6).split('')
}

/*************************************************
*                                                *
*                     Timer Logic                *
*                                                *
*************************************************/
const timer = ref(0)
let interval = null

const startTimer = () => {
    const expiryDate = new Date(currentProcessInfo.value.expirationDate).getTime()
    const currentTime = new Date().getTime()
    timer.value = ((expiryDate - currentTime) / 1000)

    if (interval) {
		clearInterval(interval)
	}

    interval = setInterval(() => {
        if (timer.value <= 1) {
            clearInterval(interval)
        } else {
            timer.value--
        }
    }, 1000)
}

/*************************************************
*                                                *
*              Initial Request Logic             *
*                                                *
*************************************************/
const initialRequest = async () => {
    applyErrorState.value = false
    showLoader.value = true

    try {
        const res = await consultAdhesionProcess(props.processId)
        currentProcessInfo.value = res.data.data

        startTimer()
        showLoader.value = false
    } catch (error) {
        applyErrorState.value = true
        errorHandler(error.response.data, initialRequest)
    }
}

/*************************************************
*                                                *
*            Send/Resend Token Logic             *
*                                                *
*************************************************/
const sendTokenHandler = async () => {
    showOverlayLoader.value = true
    let res

    try {
        res = await finishAdhesionProcess({
            processId: props.processId,
            token: code.value.join('')
        })
        showOverlayLoader.value = true
        successHandler(res.data)
        navigateByPath(routeList.LOGIN_PATH)
    } catch (error) {
        errorHandler(error.response.data, sendTokenHandler, () => { showFeedbackToast.value = false })
    }
}

const requestNewTokenHandler = async () => {
    showOverlayLoader.value = true
    let res

    try {
        /* Generic token resend */
        const payload = { processId: props.processId }
        res = await resendFinishAdhesionProcessToken(payload)

        showOverlayLoader.value = false
        successHandler(res.data)
        navigateByPath(routeList.LOGIN_PATH)
    } catch (error) {
        errorHandler(error.response.data, requestNewTokenHandler)
    }
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    emit('showFeedbackToast', sucessObjectConstrutor(t('preActiveMode.sucessToast')))
}

const errorHandler = (data, retryFn, resolveBtnFunction) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, resolveBtnFunction)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*              Footer Button Params              *
*                                                *
*************************************************/
const inputValidator = () => {
    code.value.join('').length < 4 ? showMandatoryErrors.value = true : sendTokenHandler()
}

const footerButtonsParams = computed(() => {
    return [{
        label: t('preActiveMode.footerButtonsParams.cancel'),
        buttonPosition: 'right',
        callbackFunction: () => navigateByPath(routeList.LOGIN_PATH)
    }, {
        label: t('preActiveMode.footerButtonsParams.confirm'),
		icon: 'arrow-right-full',
        iconPosition: 'right',
        buttonType: 'primary',
        callbackFunction: inputValidator
    }]
})

initialRequest()
</script>

<style lang="scss" scoped>
.content-wrapper {

    width: 100%;
    max-width: 500px;
    margin: 0 auto;
    .text {
    @include H6-Regular;
    color: var(--secondary-dark-4);
    margin-bottom: 24px;
    }

    .token-content-wrapper {
        width: 100%;
        margin-top: 16px;

        .inputs-wrapper {
            width: 100%;
            height: auto;

            display: flex;
            flex-direction: row;
            justify-content: space-between;

            box-shadow: none;

            &.adjust-margin {
                margin-bottom: 5px;
            }
        }

        .error-message {
            @include H7-Regular;
            color: var(--negative);
            margin-top: 10px;
        }

        &.mobile {
            .inputs-wrapper {
                gap: 6px;

                .input-content {
                    width: 100%;
                    flex-shrink: 1;
                }
            }
        }
        .activationCode {
        margin-top: 16px;
        margin-bottom: 16px;

        @include H7-Regular;
        color: var(--secondary-dark-1);
        }
    }
    .resent-wrapper {
        width: 100%;
        margin-top: 16px;
        gap: 16px;

        .title-note {
            @include H7-Medium;
            color: var(--secondary-dark-2);
        }

        .hour {
            @include H3-Regular;
            color: var(--secondary);

            margin-top: 16px;
            margin-bottom: 16px;
        }
        .resend-message {
            margin-bottom: 16px;

            @include H7-Regular;
            color: var(--secondary-dark-1);
        }
    }
}

</style>
